import type {
  BannerLargeMode,
  ContentPageVariant,
} from '@pnpmworkspace/shared-types';

export type { BannerLargeMode };

export type Team = 'rex' | 'kreativ' | 'other';
export const TEAM_VALUES = [
  { name: 'REX', value: 'rex' },
  { name: 'Kreativ', value: 'kreativ' },
  { name: 'Other', value: 'other' },
];

/**
 * Configuration data type for planners. This is the type of data that is stored in firestore.
 */
export const CONTENT_PAGE_SECTION_VISIBILITY_VALUES = [
  'pageSecondaryBanner',
  'pagePrimaryCard',
  'pageSecondaryCard',
  'video',
] as const;
export type ContentPageSectionVisibilityEnum =
  (typeof CONTENT_PAGE_SECTION_VISIBILITY_VALUES)[number];
export type ContentPageSectionVisibility = Record<
  ContentPageSectionVisibilityEnum,
  boolean
>;
export function isContentPageSectionVisibility(
  value?: string,
): value is ContentPageSectionVisibilityEnum {
  if (value) {
    CONTENT_PAGE_SECTION_VISIBILITY_VALUES.includes(
      value as ContentPageSectionVisibilityEnum,
    );
  }
  return false;
}

export type PlannerConfigEntityPhraseStrings = { [key: string]: string };

type RequiredPlannerConfigEntityFields = {
  id: string;
  identifier: string;
};

type OptionalPlannerConfigEntityFields = {
  team: Team;
  appId: string;
  contentPageVariant: ContentPageVariant;
  imageUrl: string;
  inStoreImageUrl: string;
  plannerUrl: string;
  posterUrl: string;
  inverseTheme: boolean;
  plannerColor: string;
  productsInclude: string[];
  productsExclude: string[];
  rangeExclude: string[];
  rangeInclude: string[];
  searchable: boolean;
  includeDeeplinkParams: boolean;
  includeListingSearchParams: boolean;
  showInPages: string[];
  standalone: boolean;
  supportedDevices: string[];
  videoUrl: string;
  primaryCardCtaUrl: string;
  secondaryCardCtaUrl: string;
  secondaryBannerImgUrl: string;
  secondaryBannerCtaUrl: string;
  marketData: PlannerConfigMarketDataEntity[] | null;
  lastModified: string;
  modifiedBy: string;
  phraseStrings: PlannerConfigEntityPhraseStrings;
  category: string;
  plannerContact: string;
  headerVisible?: boolean;
  dynamicView: Record<string, boolean>;
  contentPageSectionVisibility: ContentPageSectionVisibility;
};

export type PlannerConfigEntity = RequiredPlannerConfigEntityFields &
  OptionalPlannerConfigEntityFields;

export type PlannerConfigEntityWithOptionalFields =
  RequiredPlannerConfigEntityFields &
    Partial<OptionalPlannerConfigEntityFields>;

/**
 * Type that represents the market specific configuration for the planner
 */
export type PlannerConfigMarketDataEntity = {
  includeDeeplinkParams: boolean;
  locale: string;
  isActive: boolean;
  imageUrl: string;
  videoUrl: string;
  primaryCardCtaUrl: string;
  secondaryBannerCtaUrl: string;
  secondaryBannerImgUrl: string;
  secondaryCardCtaUrl: string;
  productsInclude: string[];
  productsExclude: string[];
  rangeExclude: string[];
  rangeInclude: string[];
  bannerLargeMode?: BannerLargeMode;
  videoPosterUrl?: string | null;
};

const setMarketIncludeDeeplinkParams = (
  marketData?: PlannerConfigMarketDataEntity[] | null,
  globalDeeplink?: boolean,
): PlannerConfigMarketDataEntity[] => {
  return (marketData || []).map((item) => ({
    ...item,
    includeDeeplinkParams:
      item.includeDeeplinkParams ?? globalDeeplink ?? false,
  }));
};

export const plannerWithDefaultValues = (
  data: PlannerConfigEntityWithOptionalFields,
): PlannerConfigEntityWithOptionalFields => ({
  id: data.id,
  contentPageVariant: data.contentPageVariant || 'none',
  identifier: data.identifier || '',
  imageUrl: data.imageUrl || '',
  secondaryBannerImgUrl: data.secondaryBannerImgUrl || '',
  plannerUrl: data.plannerUrl || '',
  searchable: data.searchable || false,
  includeDeeplinkParams: data.includeDeeplinkParams || false,
  includeListingSearchParams: data.includeListingSearchParams || false,
  supportedDevices: data.supportedDevices,
  showInPages: data.showInPages,
  productsInclude: data.productsInclude,
  productsExclude: data.productsExclude,
  rangeInclude: data.rangeInclude,
  rangeExclude: data.rangeExclude,
  marketData: setMarketIncludeDeeplinkParams(
    data.marketData,
    data.includeDeeplinkParams,
  ),
  team: data.team,
  category: (data.team === 'kreativ' ? 'kreativ' : data.category) || '',
  contentPageSectionVisibility: data.contentPageSectionVisibility || {
    pageSecondaryBanner: true,
    pagePrimaryCard: true,
    pageSecondaryCard: true,
    video: true,
  },
  phraseStrings: data.phraseStrings ?? {},
  plannerContact: data.plannerContact || data.modifiedBy || '',
});
